<template>
	<div
		v-if="appIsInitialized"
		id="analysis"
		class="tagging-tool"
		:class="{
			mobile: isMobileViewport,
			landscape: isLandscape,
			portrait: isMobilePortrait,
			'touch-device': isTouchDevice,
		}"
	>
		<!-- loading spinner -->
		<LoadingSpinner
			v-if="showLoader"
			class="tagging-tool__spinner"
			center
		/>
		<LayoutTaggingTool>
			<template
				v-if="!isNarrowAnalysis"
				#actions-left
			>
				<TagCenter />
			</template>
			<template #media>
				<ErrorScreen
					v-if="hasErrorScreen"
					:screen="errorScreen"
				/>
				<template v-else>
					<div class="tagging-tool__media js-tagging-tool-media">
						<PlayerWrapper
							key="player"
							class="tagging-tool__player"
							:autoplay="!isMobileViewport"
							@change-duration="handleDurationUpdate"
						/>
						<div
							v-if="isMobileViewport"
							class="tagging-tool__in-player-toast-wrapper"
						>
							<InPlayerToast />
						</div>
						<div
							v-if="!isMobileViewport"
							class="tagging-tool__timeline-w js-one-tagging-tool-controls"
						>
							<TimelineBlock
								class="tagging-tool__timeline"
								:is-tagging-tool="true"
								:duration="duration"
								:elapsed="position"
							/>
						</div>
					</div>
				</template>
			</template>
			<template #actions>
				<ActionBlock :elapsed="position" />
			</template>
		</LayoutTaggingTool>
		<ServerErrorBlock />
		<!-- share -->
		<ShareModal
			v-if="showShare"
			class="tagging-tool__custom-modals"
			:title="shareTitle"
			:event-title="eventTitle"
			:item="shareItem"
			:item-label="itemLabel"
			:links="shareLinks"
			:url="shareUrl"
			:is-event="true"
			:url-label="urlLabel"
			:image-not-found-label="imageNotFoundLabel"
			@close="handleShareClose"
		/>
		<!-- context menu mobile -->
		<ContextMenuMobile
			v-if="isMobileViewport"
			class="tagging-tool__custom-modals"
		/>

		<Notifications />

		<DownloadVideoModal
			v-if="downloadVideoModal.opened"
			class="tagging-tool__custom-modals"
			v-bind="downloadVideoModal"
			@close="downloadVideoModal.opened = false"
		/>

		<!-- Time picker modal -->
		<TimePickerModalMobile v-if="isMobileViewport" />

		<ConfirmModal
			v-if="confirmReload"
			primary-btn-class="button--primary"
			:title="trans('analysis.stream_restarted')"
			:confirm-label="transUcfirst('commons.reload')"
			:cancel-label="transUcfirst('commons.cancel')"
			@confirm="handleConfirmReload"
		>
			<template #body>
				<p>
					{{ trans('analysis.stream_restarted_info') }}
				</p>
			</template>
		</ConfirmModal>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { onScroll } from '../../util/scrollbar';
import { EVENT, bus } from '@videotools/js/events';

import LayoutTaggingTool from '../components/LayoutTaggingTool';
import PlayerWrapper from '@components/player/PlayerWrapper';
import TimelineBlock from '@videotools/js/blocks/Timeline';
import ActionBlock from '../blocks/Action';
import ServerErrorBlock from '../../blocks/ServerError';
import ShareModal from '@components/share-modal';
import Notifications from '@components/Toast';
import DownloadVideoModal from '@components/event/DownloadVideoModal';
import LoadingSpinner from '@components/loader/LoadingSpinner';
import { ContextMenuMobile } from '@components/context-menu';
import InPlayerToast from '@components/notifications/InPlayerToast';
import TimePickerModalMobile from '@components/modal/TimePickerModalMobile';
import ConfirmModal from '@components/modal/ConfirmModal';
import ErrorScreen from '@videotools/js/components/ErrorScreen';
import TagCenter from '@videotools/js/analysis/components/TagCenter/TagCenter';

const SHARE_TYPE = {
	EVENT: 'event',
	TAG:   'tag',
};

const busEvents = [
	EVENT.SHARE_TAG,
	EVENT.SHARE_EVENT,
	EVENT.CLIP_DOWNLOAD_VIDEO,
	EVENT.PLAYER.ON.PROGRESS,
	EVENT.STREAM_START_CHANGED,
];

export default {
	name:       'TaggingTool',
	components: {
		TimePickerModalMobile,
		InPlayerToast,
		LayoutTaggingTool,
		ActionBlock,
		TimelineBlock,
		ServerErrorBlock,
		ShareModal,
		ContextMenuMobile,
		Notifications,
		DownloadVideoModal,
		LoadingSpinner,
		PlayerWrapper,
		ConfirmModal,
		ErrorScreen,
		TagCenter,
	},
	data() {
		return {
			// media
			duration:           0,
			position:           0,
			// share
			showShare:          false,
			shareItem:          null,
			shareType:          null,
			downloadVideoModal: {
				opened:       false,
				title:        `${this.$I18n.trans_choice('commons.video', 1)} ${this.$I18n.trans('commons.download')}`,
				event:        null,
				startSeconds: null,
				endSeconds:   null,
				tagName:      null,
			},
			confirmReload: false,
		};
	},
	computed: {
		...mapGetters({
			appIsInitialized:       'app/isInitialized',
			event:                  'event/event',
			eventTags:              'event/tags',
			eventTitle:             'event/eventTitle',
			canUpdateEvent:         'event/canUpdateEvent',
			canUpdateTagTemplates:  'permissions/canUpdateTagTemplates',
			canCreateHighlightRule: 'permissions/canCreateHighlightRule',
			isSaving:               'eventTagEdit/isSaving',
			sportName:              'event/sportName',
			isMobileViewport:       'device/isMobileViewport',
			isNarrowAnalysis:       'device/isNarrowAnalysis',
			isLandscape:            'device/isLandscape',
			isMobilePortrait:       'device/isMobilePortrait',
			isTouchDevice:          'device/isTouchDevice',
			isFullscreen:           'device/isFullscreen',
			supportsNativeShare:    'device/supportsNativeShare',
			participantById:        'event/participantById',
			hasErrorScreen:         'error/hasErrorScreen',
			errorScreen:            'error/errorScreen',
			matchTags:              'tags/matchTags',
		}),
		showLoader() {
			return this.isSaving;
		},
		shareTitle() {
			switch (this.shareType) {
			case SHARE_TYPE.EVENT:
				return this.shareLabels.SHARE_EVENT;
			case SHARE_TYPE.TAG:
				return this.shareLabels.SHARE_TAG;
			default:
				return this.shareLabels.SHARE;
			}
		},
		itemLabel() {
			const parentCategory = this.$Helpers.ucfirst(this.$I18n.trans('event.category.parent')[this.event.categoryParent] ?? '');
			const childCategory = this.$Helpers.ucfirst(this.$I18n.trans('event.category.child')[this.event.categoryChild] ?? '');
			return `${parentCategory} • ${childCategory}`;
		},
		shareUrl() {
			if (!this.shareItem) {
				return '';
			}
			return this.shareItem.url;
		},
		shareLinks() {
			return this.shareItem?.shareLinks ?? { facebook: '', twitter: '', mail: '' };
		},
		urlLabel() {
			return this.shareLabels.COPY_LINK;
		},
		imageNotFoundLabel() {
			return this.shareLabels.PREVIEW_NOT_FOUND;
		},
	},
	watch: {
		isFullscreen: {
			handler:   'lockOrientation',
			immediate: true,
		},
	},
	async created() {
		this.setAppTypeAnalysis();
		this.setAppStateInitializing();
		if (this.canCreateHighlightRule && this.canUpdateEvent) {
			await this.loadHighlightRules();
		}
		this.setAppStateNormal();

		for (const event of busEvents) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}
	},
	mounted() {
		onScroll();
	},
	destroyed() {
		for (const event of busEvents) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
	},
	methods: {
		...mapActions({
			setAppStateInitializing: 'app/setAppStateInitializing',
			setAppStateNormal:       'app/setAppStateNormal',
			setAppTypeAnalysis:      'app/setAppTypeAnalysis',
			loadHighlightRules:      'event/loadHighlightRules',
			lockOrientation:         'device/lockOrientation',
			updateEventTags:         'event/updateEventTags',
			timeShiftEventTags:      'event/timeShiftEventTags',
			shareNative:             'device/shareNative',
		}),
		[`BUS-${EVENT.SHARE_TAG}`](tag) {
			this.shareItem = tag;
			this.shareType = SHARE_TYPE.TAG;

			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative({
					url: this.shareUrl,
				});
			} else {
				this.showShare = true;
			}
		},
		[`BUS-${EVENT.SHARE_EVENT}`]() {
			this.shareItem = this.event;
			this.shareType = SHARE_TYPE.EVENT;
			this.showShare = true;
			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative({
					url:  this.shareUrl,
					text: this.eventTitle,
				});
			} else {
				this.showShare = true;
			}
		},
		[`BUS-${EVENT.CLIP_DOWNLOAD_VIDEO}`]({ event, eventTag }) {
			const { secondsBefore = null, secondsAfter = null, timestamp = null, name = null } = eventTag;
			if (secondsBefore === null || secondsAfter === null || timestamp === null || name === null) {
				throw new Error('Failed to download, invalid eventTag.');
			}

			const start = timestamp - secondsBefore;
			const end = timestamp + secondsAfter;

			this.downloadVideoModal = {
				...this.downloadVideoModal,
				opened:       true,
				event,
				startSeconds: start,
				endSeconds:   end,
				tagName:      name,
			};
		},
		[`BUS-${EVENT.PLAYER.ON.PROGRESS}`](value) {
			this.position = value;
		},
		[`BUS-${EVENT.STREAM_START_CHANGED}`]() {
			this.confirmReload = true;
		},
		handleDurationUpdate(val) {
			this.duration = val;
		},
		handleShareClose() {
			this.showShare = false;
		},
		handleConfirmReload() {
			window.location.reload();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/js/components/timeline/timeline.scss';

.tagging-tool {
	height: 100%;
}

.tagging-tool__media {
	width: 100%;
}

.tagging-tool__player {
	@include z-index('player', 'tagging-tool');
	position: relative;
	margin-bottom: $sp2;
}

.tagging-tool__timeline-w {
	@include z-index('tag-timeline', 'tagging-tool');
	position: relative;
	background-color: $timeline-background;
	padding: $sp5;
}

// .tagging-tool__timeline {}

.tagging-tool__custom-modals {
	position: absolute;
	width: 100%;
	bottom: 0;
	left: 0;
	height: auto;
}

.tagging-tool__spinner {
	@include z-index('spinner', 'tagging-tool');
	position: absolute;
	background: rgba(0, 0, 0, 0.4);
}

.tagging-tool__in-player-toast-wrapper {
	@include z-index('in-player-toast', 'tagging-tool');
	position: absolute;
	bottom: 6px;
	width: 100%;
	overflow: hidden;
}
</style>
